import { serialize } from 'object-to-formdata'

import { http } from '@/shared/api'
import type { ApiListResponse, ApiSingleResponse } from '@/shared/api'
import type { EntityId, EntityListParams, ReadOptions } from './types'

/**
 * The `object-to-formdata` settings every multipart entity here uses: Rails wants
 * `logo[]` rather than `logo[0]`, and an emptied collection has to reach the server as
 * an empty array instead of vanishing from the body (which the API reads as "unchanged").
 */
const FORM_DATA_OPTIONS = { indices: false, allowEmptyArrays: true }

/** Pass as `serializeBody` when an entity's create/update has to post multipart. */
export const toFormData = (payload: unknown) =>
  serialize(payload as Record<string, unknown>, FORM_DATA_OPTIONS)

/**
 * The five REST calls a v2 entity endpoint answers, built from its URL — an entity's
 * `api/index.ts` is this call and whatever it cannot cover.
 *
 * ```ts
 * export const { getItems, createItem, deleteItem } = createEntityApi<MalwareScan>({
 *   endpoint: API.malwareScans
 * })
 * ```
 *
 * **Destructure only the calls the endpoint actually serves.** All five are always built,
 * so what is taken is what exists — an entity with no detail route simply does not take
 * `getItem`, and `createEntityQueries` then cannot be handed one (check-fsd rule 19
 * enforces the other half of that).
 *
 * The generics are separate because the calls genuinely differ: `TListItem` is what the
 * list returns and `TDetail` what one record returns, and an update usually omits the
 * fields fixed at creation.
 *
 * Anything that is not plain REST stays a hand-written call — build its path with `itemUrl`
 * so the endpoint is still spelled once, or use `memberAction` for the common
 * `POST /endpoint/:id/<verb>` shape.
 */
export function createEntityApi<
  TListItem,
  TCreatePayload = never,
  TUpdatePayload = TCreatePayload,
  TDetail = TListItem,
  // Last and defaulted, so the four type arguments an entity already passes keep their
  // positions. Narrow it from an entity that types its own filter; left alone, the list
  // envelope is still checked and the filter inside it is not.
  TListParams extends EntityListParams = EntityListParams
>(config: {
  endpoint: string
  /** Params merged under every list call — a caller's own params win. */
  defaultParams?: Record<string, unknown>
  /** Wrap create/update bodies — pass `toFormData` for a multipart entity. */
  serializeBody?: (payload: unknown) => unknown
}) {
  const { endpoint, defaultParams, serializeBody } = config

  const body = (payload: unknown) =>
    serializeBody ? serializeBody(payload) : payload

  /** `/endpoint/:id` — the base for any custom call this factory does not cover. */
  const itemUrl = (id: EntityId) => `${endpoint}/${id}`

  /**
   * `POST /endpoint/:id/<action>` — the shape a custom member action takes. The body is
   * optional and defaults to `{}`, which is what an action carrying no payload is sent.
   */
  const memberAction =
    <TResponse = unknown>(action: string) =>
    (id: EntityId, payload: unknown = {}) =>
      http.post<TResponse>(`${itemUrl(id)}/${action}`, payload)

  // The two reads take the caller's `signal`; the three writes do not — see `ReadOptions`.
  const getItems = (
    params: TListParams = {} as TListParams,
    options?: ReadOptions
  ) =>
    http.get<ApiListResponse<TListItem>>(endpoint, {
      params: defaultParams ? { ...defaultParams, ...params } : params,
      signal: options?.signal
    })

  const getItem = (id: EntityId, options?: ReadOptions) =>
    http.get<ApiSingleResponse<TDetail>>(itemUrl(id), {
      signal: options?.signal
    })

  const createItem = (payload: TCreatePayload) =>
    http.post<ApiSingleResponse<TDetail>>(endpoint, body(payload))

  const updateItem = ({ id, data }: { id: EntityId; data: TUpdatePayload }) =>
    http.patch<ApiSingleResponse<TDetail>>(itemUrl(id), body(data))

  const deleteItem = (id: EntityId) => http.delete(itemUrl(id))

  return {
    itemUrl,
    memberAction,
    getItems,
    getItem,
    createItem,
    updateItem,
    deleteItem
  }
}
